import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Clock, PenLine, ArrowRight, Lock } from "lucide-react";

// Free-tier education about ELDs + paper logs, shown inside the guide (Step/Mission 6).
// The "how to actually do them" practice lives in the paid tier (practiceTo).
export default function PaperLogsEducation({ practiceTo = "/paper-logs", isPaid = false }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="paperlogs-education">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Your ELD backup plan</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">
        When the ELD goes down, paper logs keep you legal
      </h3>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-[0.15em] font-bold text-brand mb-2">Why you need an ELD</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Federal law requires most interstate drivers to record Hours of Service on a registered ELD. It tracks your
            driving time automatically and keeps you out of costly HOS violations that wreck your CSA score.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-[0.15em] font-bold text-brand mb-2">But ELDs fail</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Dead battery, software glitch, lost signal, truck in the shop. When it malfunctions you're not off the hook —
            you switch to <strong>paper logs</strong> (a hand-drawn Record of Duty Status) and keep running compliant.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl bg-white border-l-4 border-amber-500 p-5">
        <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          <strong>The rule:</strong> note the malfunction, reconstruct your log for the current 24 hours and the past 7 days,
          and run on paper for up to <strong>8 days</strong> while the ELD is repaired or replaced. If you can't fill out a
          paper log correctly at a roadside inspection, you risk an out-of-service order — your truck and your income stop.
        </p>
      </div>

      {/* Upsell to the paid practice tool */}
      <div className="mt-5 rounded-xl bg-slate-900 text-white p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-brand mb-1">
            <PenLine className="w-3.5 h-3.5" /> {isPaid ? "Pro tool" : "Included with Pro"}
          </div>
          <p className="font-heading font-bold text-lg leading-tight">Practice doing a paper log the right way</p>
          <p className="text-sm text-slate-400 mt-1 leading-relaxed max-w-md">
            Draw a full 24-hour grid, get instant feedback against the 11 / 14 / 8-hour and 30-minute-break rules, and test
            yourself with a quick quiz.
          </p>
        </div>
        <Link to={practiceTo} data-testid="paperlogs-practice-cta" className="shrink-0">
          <Button className="rounded-full bg-brand hover:bg-brand-hover text-white font-bold h-12 px-6 group">
            {!isPaid && <Lock className="w-4 h-4 mr-2" />}
            {isPaid ? "Open Paper Log Practice" : "Practice in Pro"}
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
